import { ApiError, post } from '@/api/client';
import { forgetDestination } from '@/auth/destination';
import { useAuth } from '@/auth/store';
import { T } from '@/i18n';
import { commitGeneration } from '@/shell/context';
import { Button } from '@/ui';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { verificationText } from './OpprettBedrift';
import { KontoBox, failureText, hint } from './shared';

interface Made {
  tenant: { id: number; name: string };
  role: string;
  token: string | null;
}

/**
 * /konto/demo: where the site's "Prøv demo" lands after signup (plan 0028 slices 1 and 2). Asks
 * for the person's private demo, made on the first visit and the same one after, and opens it.
 */
export default function DemoStart() {
  const nav = useNavigate();
  const user = useAuth((s) => s.user);
  const setSession = useAuth((s) => s.setSession);
  const [err, setErr] = useState<string | null>(null);
  const [unverified, setUnverified] = useState(false);
  const [resend, setResend] = useState<string | null>(null);
  const started = useRef(false); // StrictMode runs the effect twice; the server makes one demo either way

  async function open() {
    setErr(null);
    try {
      const r = await post<Made>('/account/demo', {});
      if (!r.token || !user) return setErr(T('Demoen ble laget, men kunne ikke åpnes. Logg inn på nytt.'));
      setSession(r.token, { ...user, role: r.role });
      commitGeneration(user.id, null, null); // the token names the demo; nothing from before is readable
      forgetDestination();
      nav('/', { replace: true });
    } catch (ex) {
      if (ex instanceof ApiError && ex.code === 'verify_email_first') setUnverified(true);
      else if (ex instanceof ApiError && ex.status === 404) {
        // demos are not offered here: the destination is dropped, so the next login does not land here again
        forgetDestination();
        nav('/konto/opprett-bedrift', { replace: true });
      } else setErr(failureText(ex));
    }
  }
  // made on arrival only for somebody with no company yet, the site's visitor; a person who already
  // works somewhere and follows a link here is asked first, so their session does not switch
  const visitor = user?.role == null;
  const [asked, setAsked] = useState(visitor);
  useEffect(() => {
    if (!asked || started.current) return;
    started.current = true;
    void open();
  }); // every render, guarded: `open` is a new function each time, and the ref keeps it to one call

  async function again() {
    try {
      const r = await post<{ verified: boolean }>('/account/verification', {});
      setResend(verificationText(r.verified ? 200 : 202, null));
    } catch (ex) {
      setResend(
        ex instanceof ApiError ? verificationText(ex.status, ex.code, ex.message) : verificationText(0, null),
      );
    }
  }

  return (
    <KontoBox title={T('Din demobedrift')}>
      <div className="opt" style={{ display: 'grid', gap: 10 }}>
        {unverified ? (
          <>
            <div className="al dgr" style={{ margin: 0 }}>
              {T('Bekreft e-postadressen din først. Se etter lenken i innboksen.')}
            </div>
            <Button onClick={again}>{T('Send lenken på nytt')}</Button>
            {resend && <output className="al info">{resend}</output>}
            <Button
              ghost
              onClick={() => {
                setUnverified(false);
                void open();
              }}
            >
              {T('Jeg har bekreftet, prøv igjen')}
            </Button>
          </>
        ) : err ? (
          <>
            <div className="al dgr" style={{ margin: 0 }}>
              {err}
            </div>
            <Button onClick={() => void open()}>{T('Prøv igjen')}</Button>
          </>
        ) : asked ? (
          <span style={hint}>{T('Gjør klar demobedriften din …')}</span>
        ) : (
          <>
            <span style={hint}>
              {T('En demobedrift med oppdiktede personer, bare for deg. Du kan bytte tilbake når som helst.')}
            </span>
            <Button tone="pri" onClick={() => setAsked(true)}>
              {T('Åpne demobedriften')}
            </Button>
          </>
        )}
        <Link to="/konto/opprett-bedrift" style={hint}>
          {T('Vil du heller sette opp din egen bedrift?')}
        </Link>
      </div>
    </KontoBox>
  );
}
